import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../../context/AuthContext"
import "./auth.css"

/**
 * AuthModal — combined Login + Register popup
 * Props:
 *   isOpen  : boolean
 *   onClose : fn
 *   mode    : "login" | "register"  (default: "login")
 */
const AuthModal = ({ isOpen, onClose, mode = "login" }) => {
  const { userLogin, userRegister } = useAuth()
  const navigate = useNavigate()

  const [tab, setTab]     = useState(mode)     // "login" | "register"
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [loading, setLoading] = useState(false)

  // Login form
  const [login, setLogin] = useState({ email: "", password: "" })

  // Register form
  const [reg, setReg] = useState({ name: "", email: "", phone: "", password: "", confirm: "" })

  // Sync tab when parent changes mode
  useEffect(() => { setTab(mode) }, [mode])

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setError(""); setSuccess(""); setLoading(false)
      setLogin({ email: "", password: "" })
      setReg({ name: "", email: "", phone: "", password: "", confirm: "" })
    }
  }, [isOpen])

  // Close on Escape key
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose() }
    if (isOpen) document.addEventListener("keydown", handler)
    return () => document.removeEventListener("keydown", handler)
  }, [isOpen, onClose])

  // Prevent body scroll when modal open
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : ""
    return () => { document.body.style.overflow = "" }
  }, [isOpen])

  if (!isOpen) return null

  // ── Login submit ──
  const handleLogin = async (e) => {
    e.preventDefault()
    setError(""); setLoading(true)
    try {
      await userLogin(login.email, login.password)
      onClose()
      navigate("/dashboard")
    } catch (err) {
      setError(err.response?.data?.message || "Login failed. Please check your credentials.")
    } finally {
      setLoading(false)
    }
  }

  // ── Register submit ──
  const handleRegister = async (e) => {
    e.preventDefault()
    setError("")
    if (!reg.name.trim())         return setError("Full name is required.")
    if (!reg.email.trim())        return setError("Email is required.")
    if (reg.password.length < 6)  return setError("Password must be at least 6 characters.")
    if (reg.password !== reg.confirm) return setError("Passwords do not match.")
    setLoading(true)
    try {
      await userRegister(reg.name.trim(), reg.email.trim(), reg.phone.trim(), reg.password)
      onClose()
      navigate("/dashboard")
    } catch (err) {
      setError(err.response?.data?.message || "Registration failed. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {/* Backdrop */}
      <div className="modal-backdrop" onClick={onClose} />

      {/* Modal box */}
      <div className="auth-modal" role="dialog" aria-modal="true">

        {/* Close button */}
        <button className="modal-close-btn" onClick={onClose} aria-label="Close">
          <i className="fa fa-times"></i>
        </button>

        {/* Logo */}
        <div className="auth-logo">
          <img src="/images/logo.png" alt="Rovers Access Limited" />
        </div>

        {/* Tab switcher */}
        <div className="auth-tabs">
          <button
            className={`auth-tab-btn ${tab === "login" ? "active" : ""}`}
            onClick={() => { setTab("login"); setError("") }}
          >
            Sign In
          </button>
          <button
            className={`auth-tab-btn ${tab === "register" ? "active" : ""}`}
            onClick={() => { setTab("register"); setError("") }}
          >
            Create Account
          </button>
        </div>

        {/* Alerts */}
        {error   && <div className="auth-error"><i className="fa fa-exclamation-circle"></i> {error}</div>}
        {success && <div className="auth-success"><i className="fa fa-check-circle"></i> {success}</div>}

        {/* ── LOGIN form ── */}
        {tab === "login" && (
          <>
            <p className="auth-sub">Sign in to access your trading courses</p>
            <form onSubmit={handleLogin}>
              <div className="auth-field">
                <label>Email Address</label>
                <input
                  type="email" placeholder="you@example.com"
                  value={login.email}
                  onChange={e => setLogin({ ...login, email: e.target.value })}
                  required autoFocus
                />
              </div>
              <div className="auth-field">
                <label>Password</label>
                <input
                  type="password" placeholder="••••••••"
                  value={login.password}
                  onChange={e => setLogin({ ...login, password: e.target.value })}
                  required
                />
              </div>
              <button type="submit" className="auth-btn" disabled={loading}>
                {loading
                  ? <><i className="fa fa-spinner fa-spin"></i> Signing in…</>
                  : <><i className="fa fa-sign-in-alt"></i> Sign In</>
                }
              </button>
            </form>
            <p className="auth-switch">
              No account?{" "}
              <button className="auth-link-btn" onClick={() => { setTab("register"); setError("") }}>
                Create one free
              </button>
            </p>
          </>
        )}

        {/* ── REGISTER form ── */}
        {tab === "register" && (
          <>
            <p className="auth-sub">Join Rovers Access — Learn, Trade, Grow</p>
            <form onSubmit={handleRegister}>
              <div className="auth-field">
                <label>Full Name <span className="req">*</span></label>
                <input
                  type="text" name="name" placeholder="John Kamau"
                  value={reg.name}
                  onChange={e => setReg({ ...reg, name: e.target.value })}
                  required autoFocus
                />
              </div>
              <div className="auth-row">
                <div className="auth-field">
                  <label>Email <span className="req">*</span></label>
                  <input
                    type="email" name="email" placeholder="you@example.com"
                    value={reg.email}
                    onChange={e => setReg({ ...reg, email: e.target.value })}
                    required
                  />
                </div>
                <div className="auth-field">
                  <label>Phone <span className="opt">(optional)</span></label>
                  <input
                    type="tel" name="phone" placeholder="+254 7XX XXX XXX"
                    value={reg.phone}
                    onChange={e => setReg({ ...reg, phone: e.target.value })}
                  />
                </div>
              </div>
              <div className="auth-row">
                <div className="auth-field">
                  <label>Password <span className="req">*</span></label>
                  <input
                    type="password" name="password" placeholder="Min. 6 characters"
                    value={reg.password}
                    onChange={e => setReg({ ...reg, password: e.target.value })}
                    required
                  />
                </div>
                <div className="auth-field">
                  <label>Confirm Password <span className="req">*</span></label>
                  <input
                    type="password" name="confirm" placeholder="Repeat password"
                    value={reg.confirm}
                    onChange={e => setReg({ ...reg, confirm: e.target.value })}
                    required
                  />
                </div>
              </div>
              <button type="submit" className="auth-btn" disabled={loading}>
                {loading
                  ? <><i className="fa fa-spinner fa-spin"></i> Creating account…</>
                  : <><i className="fa fa-user-plus"></i> Create Account</>
                }
              </button>
            </form>
            <p className="auth-switch">
              Already have an account?{" "}
              <button className="auth-link-btn" onClick={() => { setTab("login"); setError("") }}>
                Sign in
              </button>
            </p>
          </>
        )}

      </div>
    </>
  )
}

export default AuthModal
